/**
 * External Imports
 */
import { FC, useEffect, useLayoutEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Internal Imports
 */
import EmployeeProfileHeader from '../../components/profileHeaders/employeeProfileHeader';
import Round from '../../components/round';
import Loading from '../../components/shared/Loading';
import HistorySideNav from '../../components/sideNav/historySideNav';
import HistorySideDrawer from '../../components/sideNav/historySideDrawer';

import { RootState } from '../../redux/store';
import {
  getEmployeeRoundHistory,
  getTasksByEmployeeId,
  getQuestionBank,
  clearEmployeeRoundHistory
} from '../../redux/actions';
import {
  useGetEmployeeNotesQuery,
  useGetEmployeeToRoundQuery
} from '../../redux/apis/Employees.api';

/**
 * Global Type Definition Imports
 */
import { Feedback, Issue, RoundHistory } from '../../config/interfaces';

/**
 * Style Imports
 */
import { ProfileContainer } from './styles/Employee.styles';

interface EmployeeProfileProps {
  employeeRoundHistory?: RoundHistory[];
  issues?: Issue[];
  feedback?: Feedback[];
}

const EmployeeProfile: FC<EmployeeProfileProps> = ({
  employeeRoundHistory,
  issues,
  feedback
}) => {
  const dispatch = useDispatch();

  const [selectedTab, setSelectedTab] = useState<string>('');

  const { state } = useLocation();
  const navigate = useNavigate();
  const { employeeId = '' } =
    (state as {
      employeeId: string;
    }) || {};

  const {
    data: employeeToRoundData,
    // isError: isGetEmployeeToRoundError,
    isFetching: isGetEmployeeToRoundFetching
    // isLoading: isGetEmployeeToRoundLoading
  } = useGetEmployeeToRoundQuery(employeeId, {
    refetchOnMountOrArgChange: true,
    skip: !employeeId
  });
  const {
    data: employeeNotesData,
    // isError: isGetEmployeeNotesError,
    // isFetching: isGetEmployeeNotesFetching,
    isLoading: isGetEmployeeNotesLoading
  } = useGetEmployeeNotesQuery(employeeToRoundData?.employeeId, {
    pollingInterval: 60000,
    refetchOnMountOrArgChange: true,
    skip: !employeeToRoundData?.employeeId
  });

  useLayoutEffect(() => {
    // Perform layout effect logic here
    if (employeeToRoundData) {
      dispatch(
        getEmployeeRoundHistory({
          employeeId: employeeToRoundData.hcaid,
          roundingTypes: ['employee', 'csrn', 'cned']
        })
      );
      dispatch(
        getTasksByEmployeeId({
          employeeId: employeeToRoundData?.hcaid,
          roundingTypes: ['employee', 'cned', 'csrn']
        })
      );
    } else if (!employeeId) {
      // TODO: Hack to return to patient census when refreshing or navigating straight to profile
      navigate('/employees/list/user');
    }
  }, [employeeToRoundData]);

  useEffect(() => {
    return () => {
      dispatch(clearEmployeeRoundHistory());
    };
  }, []);

  useEffect(() => {
    if (employeeToRoundData) {
      dispatch(getQuestionBank());
    }
  }, [employeeToRoundData]);

  return (
    <ProfileContainer>
      {isGetEmployeeToRoundFetching || isGetEmployeeNotesLoading ? (
        <Loading centered label="employee data..." />
      ) : (
        <>
          <section>
            <EmployeeProfileHeader selectedEmployee={employeeToRoundData} />
          </section>
          <section className="d-flex" style={{ height: 'calc(100vh - 163px)' }}>
            <HistorySideNav
              selectedEmployee={{
                ...employeeToRoundData,
                notes: employeeNotesData || []
              }}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              type="employee"
            />
            <HistorySideDrawer
              selectedTab={selectedTab}
              selectedPerson={{
                ...employeeToRoundData,
                notes: employeeNotesData || []
              }}
              roundHistory={employeeRoundHistory}
              tasks={[...(issues || []), ...(feedback || [])]}
              type="employee"
              setSelectedTab={setSelectedTab}
            />
            <Round type="employee" selectedEmployee={employeeToRoundData} />
          </section>
        </>
      )}
    </ProfileContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    employeeRoundHistory: state.ReportReducer.employeeRoundHistory,
    feedback: state.TaskReducer.employeeFeedback,
    issues: state.TaskReducer.employeeIssues
  };
};

export default connect(mapReduxStateToProps)(EmployeeProfile);
